import { ChangeDetectionStrategy, Component, Injectable } from '@angular/core';
import {
	CachePolicy,
	CachePolicyEntity,
	combineAll,
	DcBaseComponent,
	UrlService,
} from '@datachain/ui-sdk/common';
import { AvatarEntity } from '@datachain/ui-sdk/components/avatar';
import { concat, map, Observable, of, switchMap, tap } from 'rxjs';

import { DcBaseRequester } from '../../../requesters/core/dc-base.requester';
import { DcIcons } from '../../app-dc.icons';

interface IUserDetails {
	id: string;
	enabled: boolean;
	first_name: string;
	last_name: string;
	name: string;
	unknown: boolean;
}

interface IMember {
	avatar: AvatarEntity | null;
	name: string;
	isGroup: boolean;
}

@Injectable({
	providedIn: 'root',
})
class DcUserRequester extends DcBaseRequester<IUserDetails> {
	private readonly userMappings: Map<string, IMember>;
	protected resource;
	protected elementsListUrl;
	protected singleElementUrl;

	public constructor(private readonly urlService: UrlService) {
		super();
		this.userMappings = new Map<string, IMember>();
		this.resource = this.generateResourceUrl('USER_BASIC');
		this.elementsListUrl = this.resource;
		this.singleElementUrl = this.resource;
	}

	public getUserDetails(uuid: string, isGroup: boolean): Observable<IMember> {
		const maybeUserAvatar = this.userMappings.get(uuid);
		if (maybeUserAvatar) {
			return of(maybeUserAvatar);
		}
		const url = this.urlService.generateUrl(
			this.resource,
			{
				userId: uuid,
			},
			{
				isGroup,
			}
		);

		return this.get<IUserDetails>(
			url,
			CachePolicyEntity.build({
				policy: CachePolicy.CacheOnly,
				maxAgeInSeconds: 5 * 60,
				isCacheable: true,
			})
		).pipe(
			map((details) => {
				const avatar = !isGroup ? AvatarEntity.build(details.name, 2) : null;
				const memberDetails: IMember = {
					avatar,
					name: details.name,
					isGroup,
				};
				this.userMappings.set(details.id, memberDetails);
				return memberDetails;
			})
		);
	}
}

@Component({
	selector: 'app-dc-user',
	templateUrl: './app-dc-user.component.html',
	styleUrls: ['./app-dc-user.component.scss'],
	inputs: ['dcUserUuid', 'isGroup'],
	exportAs: 'DcMember',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppDcUserComponent extends DcBaseComponent {
	protected readonly DcIcons = DcIcons;

	public vo$: Observable<{
		avatar: AvatarEntity | null;
		isGroup: boolean;
	}>;

	private memberName = '';

	public constructor(private readonly dcUserRequester: DcUserRequester) {
		super();

		this.vo$ = combineAll({
			uuid: this.toObservable<string>('dcUserUuid'),
			isGroup: concat(of(false), this.toObservable<boolean>('isGroup')),
		}).pipe(
			switchMap(({ uuid, isGroup }) =>
				this.dcUserRequester.getUserDetails(uuid, isGroup)
			),
			tap((details) => (this.memberName = details.name)),
			map((details) => ({
				avatar: details.avatar,
				isGroup: details.isGroup,
			}))
		);
	}

	public getMemberName(): string {
		return this.memberName;
	}
}
